import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Pencil } from "lucide-react";
import { recalcItem, isSteelType, steelTypeOptions, SteelDimsEditor, round2 } from "@/lib/steelCalc";
import { PRODUCT_UNITS } from "@/pages/Products";

const blankItem = () => ({
  product_id: "", product_name: "", size: "", quantity: 1, unit: "KG", unit_price: 0,
  discount: 0, discount_pct: 0, vat_rate: 20, vat_amount: 0, total: 0, calc_type: "", thickness: 0, width: 0, height: 0, length: 0, unit_kg: 0, total_kg: 0, net_kg: 0, fire_pct: 0, note: "",
});

export function SalesList() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = async () => setItems((await api.get("/sales")).data);
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Bu satış kaydını silmek istediğinizden emin misiniz?")) return; await api.delete(`/sales/${id}`); toast.success("Silindi"); load(); };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Satışlar</h1>
          <p className="text-sm text-muted-foreground">Toplam {items.length} satış</p>
        </div>
        <Button onClick={() => nav("/satislar/yeni")} data-testid="new-sale-btn"><Plus size={16} className="mr-1" /> Yeni Satış</Button>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tarih</TableHead><TableHead>Fatura No</TableHead><TableHead>Müşteri</TableHead>
            <TableHead className="text-right">Ürün Sayısı</TableHead>
            <TableHead className="text-right">Toplam</TableHead>
            <TableHead className="text-right">Ödenen</TableHead>
            <TableHead className="text-right">Kalan</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{fmtDate(s.date)}</TableCell>
                <TableCell className="font-mono text-sm font-semibold">{s.number || "-"}</TableCell>
                <TableCell className="font-medium">{s.customer_name}</TableCell>
                <TableCell className="text-right">{s.items.length}</TableCell>
                <TableCell className="text-right num font-semibold">{fmtTL(s.total)}</TableCell>
                <TableCell className="text-right num text-emerald-500">{fmtTL(s.paid)}</TableCell>
                <TableCell className="text-right num text-primary">{fmtTL(s.total - s.paid)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => nav(`/satislar/${s.id}/duzenle`)} data-testid={`sale-edit-${s.id}`} title="Düzenle"><Pencil size={14} /></Button>}
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(s.id)} data-testid={`sale-delete-${s.id}`} title="Sil"><Trash2 size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Henüz satış yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function NewSale() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState({ banks: [], cash: [] });
  const [useVD, setUseVD] = useState(false);
  const [number, setNumber] = useState("");
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { id } = useParams();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    customer_id: params.get("customer") || "", items: [], date: "",
    vat_rate: 20, paid: 0, payment_method: "nakit",
    account_id: "", account_type: "cash", due_date: "", note: "",
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/settings/company").then((r) => setUseVD(!!r.data.use_vat_discount));
    if (isEdit) {
      api.get(`/sales/${id}`).then((r) => {
        const s = r.data;
        setNumber(s.number || "");
        setForm({
          customer_id: s.customer_id || "",
          items: (s.items || []).map((it) => ({
            ...blankItem(),
            product_id: it.product_id || "", product_name: it.product_name || "", size: it.size || "",
            quantity: it.quantity || 0, unit: it.unit || "KG", unit_price: it.unit_price || 0,
            discount: it.discount || 0, discount_pct: it.discount_pct || 0, vat_rate: it.vat_rate ?? 20, total: it.total || 0,
            calc_type: it.calc_type || "", thickness: it.thickness || 0, width: it.width || 0,
            height: it.height || 0, length: it.length || 0, unit_kg: it.unit_kg || 0, total_kg: it.total_kg || 0, net_kg: it.net_kg || 0, fire_pct: it.fire_pct || 0, note: it.note || "",
          })),
          date: (s.date || "").slice(0, 10),
          vat_rate: s.vat_rate ?? 20, paid: s.paid || 0,
          payment_method: s.payment_method || "nakit",
          account_id: s.account_id || "", account_type: s.account_type || "cash",
          due_date: (s.due_date || "").slice(0, 10), note: s.note || "",
        });
        setLoading(false);
      }).catch(() => { toast.error("Satış yüklenemedi"); nav("/satislar"); });
    }
  }, [id]);

  const addRow = () => setForm({ ...form, items: [...form.items, blankItem()] });
  const setRow = (i, patch) => {
    const items = [...form.items];
    items[i] = recalcItem({ ...items[i], ...patch });
    setForm({ ...form, items });
  };
  const rmRow = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const subtotal = round2(form.items.reduce((s, i) => s + round2(i.total), 0));
  const discountTotal = round2(form.items.reduce((s, i) => s + (Number(i.discount) || 0), 0));
  const vat_amount = useVD ? round2(form.items.reduce((s, i) => s + round2((Number(i.total) || 0) * (Number(i.vat_rate) || 0) / 100), 0)) : 0;
  const total = round2(subtotal + vat_amount);

  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (saving) return;
    if (!form.customer_id) return toast.error("Müşteri seçin");
    if (form.items.length === 0) return toast.error("Ürün ekleyin");
    if (form.items.some((i) => !i.product_name?.trim() || i.quantity <= 0)) return toast.error("Ürün adı ve miktar girin");
    if (form.items.some((i) => isSteelType(i.calc_type) && !(Number(i.unit_kg) > 0)))
      return toast.error("Otomatik kg satırları için tüm ölçüleri (mm) girin");
    if (form.paid > 0 && !form.account_id) return toast.error("Ödeme için hesap seçin");
    setSaving(true);
    try {
      const body = { ...form, use_vat_discount: useVD, subtotal, discount: discountTotal, vat_amount, total, paid: Number(form.paid || 0), due_date: form.due_date || null, date: form.date || null };
      if (!body.paid) { body.account_id = null; body.account_type = null; body.payment_method = null; }
      if (isEdit) {
        await api.put(`/sales/${id}`, body);
        toast.success("Satış başarıyla güncellendi");
      } else {
        await api.post("/sales", body);
        toast.success("Satış başarıyla kaydedildi");
      }
      nav("/satislar");
    } catch (e) { if (e.response && e.response.status < 500) toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const allAccounts = [...accounts.banks.map((b) => ({ ...b, type: "bank" })), ...accounts.cash.map((c) => ({ ...c, type: "cash" }))];

  if (loading) return <div className="p-8 text-center text-muted-foreground" data-testid="sale-edit-loading">Yükleniyor...</div>;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="new-sale-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav(-1)}><ArrowLeft size={18} /></Button>
        <h1 className="text-3xl font-extrabold tracking-tight">{isEdit ? "Satışı Düzenle" : "Yeni Satış"}</h1>
        {isEdit && number && <span className="font-mono text-base px-3 py-1 rounded-md bg-muted font-semibold" data-testid="sale-number-badge">{number}</span>}
      </div>
      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Müşteri *</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="sale-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
              <SelectContent className="bg-popover">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Satış Tarihi</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="sale-date-input" />
          </div>
          <div>
            <Label>Vade Tarihi</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Ürünler</Label>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="add-sale-item-btn"><Plus size={14} className="mr-1" /> Satır Ekle</Button>
          </div>
          <datalist id="sale-product-list">
            {products.map((p) => <option key={p.id} value={`${p.name}${p.size ? " · " + p.size : ""}`} />)}
          </datalist>
          <datalist id="sale-unit-list">
            {PRODUCT_UNITS.map((u) => <option key={u} value={u} />)}
          </datalist>
          <div className="space-y-3">
            {form.items.map((it, i) => {
              const isAuto = isSteelType(it.calc_type);
              return (
                <div key={i} className="border border-border rounded-md p-3 space-y-3" data-testid={`sale-item-${i}`}>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-xs text-muted-foreground">Ürün</Label>
                      <Input list="sale-product-list" value={it.product_name}
                        placeholder="Ürün adı yazın veya seçin"
                        onChange={(e) => {
                          const val = e.target.value;
                          const match = products.find((p) => `${p.name}${p.size ? " · " + p.size : ""}` === val || p.name === val);
                          if (match) {
                            setRow(i, { product_id: match.id, product_name: match.name, size: match.size || "", unit: (match.unit || "ADET").toUpperCase(), unit_price: match.sale_price });
                          } else {
                            setRow(i, { product_id: "", product_name: val });
                          }
                        }}
                        className="h-9 mt-1" data-testid={`sale-product-input-${i}`} />
                    </div>
                    <div className="w-48">
                      <Label className="text-xs text-muted-foreground">Hesaplama Türü</Label>
                      <Select value={it.calc_type || "normal"} onValueChange={(v) => setRow(i, { calc_type: v === "normal" ? "" : v })}>
                        <SelectTrigger className="h-9 mt-1" data-testid={`sale-calc-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover">
                          <SelectItem value="normal">Normal</SelectItem>
                          {steelTypeOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="ghost" size="icon" className="mb-0.5" onClick={() => rmRow(i)} data-testid={`sale-remove-item-${i}`}><Trash2 size={14} /></Button>
                  </div>

                  {isAuto ? (
                    <SteelDimsEditor item={it} onPatch={(patch) => setRow(i, patch)} idPrefix={`sale-${i}`} />
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Birim</Label>
                        <Input list="sale-unit-list" value={it.unit} onChange={(e) => setRow(i, { unit: e.target.value })} placeholder="KG / ADET..." className="h-9 mt-1" data-testid={`sale-unit-${i}`} />
                      </div>
                      <div><Label className="text-xs text-muted-foreground">Miktar</Label><Input type="number" step="0.01" value={it.quantity} onChange={(e) => setRow(i, { quantity: Number(e.target.value) })} className="h-9 mt-1 text-right" data-testid={`sale-qty-${i}`} /></div>
                      <div><Label className="text-xs text-muted-foreground">Birim Fiyat (₺)</Label><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setRow(i, { unit_price: Number(e.target.value) })} className="h-9 mt-1 text-right" data-testid={`sale-unitprice-${i}`} /></div>
                      <div><Label className="text-xs text-muted-foreground">Toplam (₺)</Label><div className="h-9 mt-1 flex items-center justify-end px-2 rounded-md bg-muted/50 num font-bold text-primary" data-testid={`sale-item-total-${i}`}>{fmtTL(it.total)}</div></div>
                    </div>
                  )}

                  {useVD && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">İskonto %</Label>
                        <Input type="number" step="0.01" value={it.discount_pct} onChange={(e) => setRow(i, { discount_pct: Number(e.target.value) })} className="h-9 mt-1 text-right" data-testid={`sale-discount-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">KDV %</Label>
                        <Select value={String(it.vat_rate ?? 20)} onValueChange={(v) => setRow(i, { vat_rate: Number(v) })}>
                          <SelectTrigger className="h-9 mt-1" data-testid={`sale-vat-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-popover">{[0, 1, 10, 20].map((r) => <SelectItem key={r} value={String(r)}>%{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 flex items-end">
                        <div className="text-xs text-muted-foreground">KDV Tutarı: <span className="num font-semibold text-foreground">{fmtTL(round2((Number(it.total) || 0) * (Number(it.vat_rate) || 0) / 100))}</span></div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs text-muted-foreground">Açıklama / Not</Label>
                    <Input value={it.note} onChange={(e) => setRow(i, { note: e.target.value })} placeholder="Opsiyonel" className="h-9 mt-1" data-testid={`sale-item-note-${i}`} />
                  </div>
                </div>
              );
            })}
            {form.items.length === 0 && <div className="text-center py-6 text-muted-foreground border border-dashed border-border rounded-md">Ürün eklenmedi</div>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div><Label>Açıklama</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <div><Label>Alınan Ödeme</Label><Input type="number" step="0.01" value={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.value })} data-testid="sale-paid-input" /></div>
            {Number(form.paid) > 0 && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Yöntem</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover"><SelectItem value="nakit">Nakit</SelectItem><SelectItem value="havale">Havale/EFT</SelectItem><SelectItem value="kart">Kredi Kartı</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hesap</Label>
                  <Select value={form.account_id} onValueChange={(v) => {
                    const a = allAccounts.find((x) => x.id === v);
                    setForm({ ...form, account_id: v, account_type: a?.type || "cash" });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-popover">{allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type === "bank" ? "Banka" : "Kasa"})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          <div className="bg-muted/40 rounded-md p-4 space-y-2">
            <div className="flex justify-between text-sm"><span>Ara Toplam{useVD ? " (KDV hariç)" : ""}</span><span className="num">{fmtTL(subtotal)}</span></div>
            {useVD && discountTotal > 0 && <div className="flex justify-between text-sm"><span>İskonto</span><span className="num text-amber-600">-{fmtTL(discountTotal)}</span></div>}
            {useVD && <div className="flex justify-between text-sm"><span>KDV Tutarı</span><span className="num">{fmtTL(vat_amount)}</span></div>}
            <div className="flex justify-between text-lg font-bold border-t border-border pt-2"><span>{useVD ? "Genel Toplam" : "Toplam"}</span><span className="num text-primary" data-testid="sale-grand-total">{fmtTL(total)}</span></div>
            <div className="flex justify-between text-sm text-emerald-500"><span>Ödenen</span><span className="num">{fmtTL(Number(form.paid) || 0)}</span></div>
            <div className="flex justify-between text-sm font-semibold"><span>Kalan Borç</span><span className="num">{fmtTL(total - (Number(form.paid) || 0))}</span></div>
            <Button className="w-full mt-3 h-11" onClick={submit} disabled={saving} data-testid="sale-submit-btn">{saving ? "Kaydediliyor..." : (isEdit ? "Değişiklikleri Kaydet" : "Satışı Kaydet")}</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
